import { VideoPlayerContent } from '@/components/reviews/Player/VideoPlayerContent';
import { ReviewType } from '@/components/reviews/types';
import { MainHeader, SectionParagraph } from '@/components/shared/Content';
import { useShowHeaderCtaOnScroll } from '@/hooks/useShowHeaderCtaOnScroll';
import videoDesktop from '@/static/assets/car/Explainer_16x9.mp4';
import videoMobile from '@/static/assets/car/Explainer_9x16.webm';
import { Flex, LegacyText, font, themedColor, useViewportSize } from '@lemonade-hq/cdk';
import { DeleteIcon, Logo, MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { useAnalyticsContext } from '@lemonade-hq/nuiza';
import parse from 'html-react-parser';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { CSSTransition } from 'react-transition-group';
import styled, { keyframes } from 'styled-components';
import JoinCTA from '../CTA';
import { CarPageContext } from '../CarPageContext';
import { MessageContainer } from './Common';

const FadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const StyledDesktopIcon = styled(DeleteIcon).attrs({ colorOverride: 'white' })`
  position: absolute;
  right: -40px;
  top: -5px;
  cursor: pointer;
  animation: ${FadeIn} 0.3s ease-in-out;
  border-radius: 50%;

  ${MEDIA_QUERIES.mobile`display: none;`}
`;

const StyledMobileIcon = styled(DeleteIcon).attrs({ colorOverride: '#7b7b7b' })`
  position: absolute;
  right: 1.4rem;
  top: 1.4rem;
  cursor: pointer;
  animation: ${FadeIn} 0.3s ease-in-out;
  border-radius: 50%;
  background-color: white;
  z-index: 2;

  ${MEDIA_QUERIES.tabletAndDesktop`display: none;`}
`;

const MobileVideo = styled.div`
  aspect-ratio: 9 / 16;
  width: min(80%, 480px);
  border-radius: 8px;
  position: relative;
`;

const DesktopVideo = styled.div`
  aspect-ratio: 16 / 9;
  height: min(80%, 480px);
  border-radius: 8px;
  position: relative;
`;

const VideoWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1900;
  backdrop-filter: blur(3px);
`;

const Note = styled(LegacyText).attrs({ type: 'main' })`
  ${font('main', { fontSize: '16px', fontWeight: 400 })}
  margin-top: 21px;
  color: ${themedColor('primary')};
  cursor: pointer;

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '18px', fontWeight: 400 })}
  `}
`;

const StaticNote = styled(LegacyText).attrs({ type: 'main' })`
  ${font('main', { fontSize: '14px', fontWeight: 400 })}
  margin-top: 16px;
  color: rgba(255, 255, 255, 0.7);

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '14px', fontWeight: 400 })}
  `}
`;

interface TitleContainerProps {
  $mobilePaddingTop?: string;
}

const TitleContainer = styled(MessageContainer)<TitleContainerProps>`
  &.enter {
    opacity: 0;
  }
  &.enter-active {
    opacity: 1;
  }
  &.exit-active {
    opacity: 0;
    transform: translateY(-100%);
  }

  ${({ $mobilePaddingTop }) =>
    $mobilePaddingTop &&
    MEDIA_QUERIES.mobile`
      padding-top: ${$mobilePaddingTop};
    `}
`;

const TopFoldTitle = styled(MainHeader)`
  white-space: initial;
  ${MEDIA_QUERIES.desktop`
    white-space: pre;
  `};
`;

const TitleWrapper = styled(Flex)`
  gap: 20px;
  margin-bottom: 30px;
  width: 100%;
  max-width: 340px;
  @media (min-width: 1024px) and (max-width: 1440px) {
    margin-bottom: 15px;
    gap: 12px;
  }

  ${MEDIA_QUERIES.tablet`
    max-width: 700px;
  `}

  ${MEDIA_QUERIES.desktop`
    max-width: 100%;
  `}

  @media (min-width: 1440px) {
    margin-bottom: 40px;
  }
`;

interface TitleProps {
  isGameOn: boolean;
  isScreenCountingDown: boolean;
  title: string;
  subtitle: string;
  belowCtaText?: string;
  customUrl?: string;
  mobilePaddingTop?: string;
  excludedParams?: string[];
}

const Videos: React.FC<{
  setShowVideo: (show: boolean) => void;
}> = ({ setShowVideo }) => {
  const [isMounted, setIsMounted] = useState(false);
  const ref = React.useRef<HTMLVideoElement>(null);
  const { isMobileViewport } = useViewportSize();
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    if (isMounted) {
      ref.current?.play();
    }
  }, [isMounted]);

  return isMounted
    ? createPortal(
        <VideoWrapper onClick={() => setShowVideo(false)}>
          {isMobileViewport ? (
            <MobileVideo onClick={e => e.stopPropagation()}>
              <StyledMobileIcon onClick={() => setShowVideo(false)} />
              <VideoPlayerContent
                review={{
                  videoUrl: videoMobile,
                  id: 1,
                  reviewerName: '',
                  captionsUrl: '',
                  type: ReviewType.Video,
                }}
                isActive={true}
                onEnd={() => null}
                isMuted={isMuted}
                setIsMuted={() => setIsMuted(muted => !muted)}
                captionsOn={false}
                showGradient={true}
              />
            </MobileVideo>
          ) : (
            <DesktopVideo onClick={e => e.stopPropagation()}>
              <StyledDesktopIcon onClick={() => setShowVideo(false)} />
              <VideoPlayerContent
                review={{
                  videoUrl: videoDesktop,
                  id: 1,
                  reviewerName: '',
                  captionsUrl: '',
                  type: ReviewType.Video,
                }}
                isActive={true}
                onEnd={() => null}
                isMuted={isMuted}
                setIsMuted={() => setIsMuted(muted => !muted)}
                captionsOn={false}
                showGradient={true}
              />
            </DesktopVideo>
          )}
        </VideoWrapper>,
        document.body,
      )
    : null;
};

const Title: React.FC<TitleProps> = ({
  isGameOn,
  isScreenCountingDown,
  title,
  subtitle,
  belowCtaText,
  customUrl,
  mobilePaddingTop,
  excludedParams,
}) => {
  const { isEmbeddedPage, analytics } = useContext(CarPageContext);
  const { trackEvent } = useAnalyticsContext();
  const { ref: mainCtaRef } = useShowHeaderCtaOnScroll();
  const [showVideo, setShowVideo] = useState(false);

  const openVideo = useCallback(() => {
    if (analytics.videoClickedEvent) trackEvent(analytics.videoClickedEvent);
    setShowVideo(true);
  }, [trackEvent, analytics.videoClickedEvent]);

  return (
    <CSSTransition in={!isGameOn && !isScreenCountingDown} unmountOnExit mountOnEnter timeout={500}>
      <TitleContainer $mobilePaddingTop={mobilePaddingTop}>
        {isEmbeddedPage && (
          <Flex justifyContent="center" mb={30} width={130}>
            <Logo />
          </Flex>
        )}
        <TitleWrapper flexDirection="column" justifyContent="center">
          <TopFoldTitle tabIndex={0}>{parse(title)}</TopFoldTitle>
          <SectionParagraph tabIndex={0}>{parse(subtitle)}</SectionParagraph>
        </TitleWrapper>
        {!isEmbeddedPage && (
          <>
            <Flex ref={mainCtaRef}>
              <JoinCTA customUrl={customUrl} excludedParams={excludedParams} />
            </Flex>
            {belowCtaText != null ? (
              <StaticNote as="span" tabIndex={-1}>
                {belowCtaText}
              </StaticNote>
            ) : (
              <Note as="a" href="#" onClick={openVideo} tabIndex={-1}>
                How our car insurance works
              </Note>
            )}
          </>
        )}

        {showVideo && !belowCtaText && <Videos setShowVideo={setShowVideo} />}
      </TitleContainer>
    </CSSTransition>
  );
};

export default React.memo(Title);
